import type { PointMm } from '../schema/primitives.js';

/**
 * Aplanado determinista de un path SVG (coordenadas en mm) a polilineas.
 *
 * Se usa para calcular la caja de una forma, comprobar que cabe en la zona y
 * medir el grosor de trazo. El numero de muestras por curva es FIJO: el
 * resultado debe ser identico en el navegador y en el servidor (DEC-10), asi
 * que no puede depender de una tolerancia adaptativa en coma flotante.
 */
export const CURVE_SAMPLES = 24;

type Cmd = { code: string; values: number[] };

/** Todo lo que no sea comando, numero o separador es basura y debe romper. */
const INVALID_CHARS = /[^MmLlHhVvCcSsQqTtAaZz0-9eE+\-.,\s]/;

function tokenize(d: string): Cmd[] {
  const invalid = INVALID_CHARS.exec(d);
  if (invalid) throw new Error(`path SVG: comando no admitido "${invalid[0]}"`);

  const cmds: Cmd[] = [];
  const re = /([MmLlHhVvCcSsQqTtAaZz])|(-?\d*\.?\d+(?:[eE][-+]?\d+)?)/g;
  let current: Cmd | null = null;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    if (m[1]) {
      current = { code: m[1], values: [] };
      cmds.push(current);
    } else if (m[2] !== undefined) {
      if (!current) throw new Error('path SVG: numero antes de cualquier comando');
      current.values.push(Number(m[2]));
    }
  }
  return cmds;
}

const bezierCubic = (p0: PointMm, p1: PointMm, p2: PointMm, p3: PointMm, t: number): PointMm => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const dd = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + dd * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + dd * p3.y,
  };
};

const bezierQuad = (p0: PointMm, p1: PointMm, p2: PointMm, t: number): PointMm => {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
};

/** Arco eliptico SVG -> curvas cubicas (algoritmo del apendice F.6 de SVG 1.1). */
function arcToCubics(
  from: PointMm,
  rx: number,
  ry: number,
  xAxisRotationDeg: number,
  largeArc: boolean,
  sweep: boolean,
  to: PointMm,
): Array<[PointMm, PointMm, PointMm]> {
  if (rx === 0 || ry === 0) return [[from, to, to]];
  const phi = (xAxisRotationDeg * Math.PI) / 180;
  const cosPhi = Math.cos(phi);
  const sinPhi = Math.sin(phi);

  const dx2 = (from.x - to.x) / 2;
  const dy2 = (from.y - to.y) / 2;
  const x1p = cosPhi * dx2 + sinPhi * dy2;
  const y1p = -sinPhi * dx2 + cosPhi * dy2;

  let rxAbs = Math.abs(rx);
  let ryAbs = Math.abs(ry);
  const lambda = (x1p * x1p) / (rxAbs * rxAbs) + (y1p * y1p) / (ryAbs * ryAbs);
  if (lambda > 1) {
    const s = Math.sqrt(lambda);
    rxAbs *= s;
    ryAbs *= s;
  }

  const sign = largeArc !== sweep ? 1 : -1;
  const num = rxAbs * rxAbs * ryAbs * ryAbs - rxAbs * rxAbs * y1p * y1p - ryAbs * ryAbs * x1p * x1p;
  const den = rxAbs * rxAbs * y1p * y1p + ryAbs * ryAbs * x1p * x1p;
  const co = sign * Math.sqrt(Math.max(0, num / den));
  const cxp = (co * rxAbs * y1p) / ryAbs;
  const cyp = (-co * ryAbs * x1p) / rxAbs;

  const cx = cosPhi * cxp - sinPhi * cyp + (from.x + to.x) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (from.y + to.y) / 2;

  const angle = (ux: number, uy: number, vx: number, vy: number): number => {
    const dot = ux * vx + uy * vy;
    const len = Math.hypot(ux, uy) * Math.hypot(vx, vy);
    let a = Math.acos(Math.max(-1, Math.min(1, dot / len)));
    if (ux * vy - uy * vx < 0) a = -a;
    return a;
  };

  const theta1 = angle(1, 0, (x1p - cxp) / rxAbs, (y1p - cyp) / ryAbs);
  let dTheta = angle((x1p - cxp) / rxAbs, (y1p - cyp) / ryAbs, (-x1p - cxp) / rxAbs, (-y1p - cyp) / ryAbs);
  if (!sweep && dTheta > 0) dTheta -= 2 * Math.PI;
  if (sweep && dTheta < 0) dTheta += 2 * Math.PI;

  const segments = Math.max(1, Math.ceil(Math.abs(dTheta) / (Math.PI / 2)));
  const delta = dTheta / segments;
  const t = ((4 / 3) * Math.tan(delta / 4));

  const out: Array<[PointMm, PointMm, PointMm]> = [];
  let th = theta1;
  for (let i = 0; i < segments; i++) {
    const cosTh1 = Math.cos(th);
    const sinTh1 = Math.sin(th);
    const th2 = th + delta;
    const cosTh2 = Math.cos(th2);
    const sinTh2 = Math.sin(th2);

    const e = (ct: number, st: number): PointMm => ({
      x: cx + rxAbs * cosPhi * ct - ryAbs * sinPhi * st,
      y: cy + rxAbs * sinPhi * ct + ryAbs * cosPhi * st,
    });
    const eDot = (ct: number, st: number): PointMm => ({
      x: -rxAbs * cosPhi * st - ryAbs * sinPhi * ct,
      y: -rxAbs * sinPhi * st + ryAbs * cosPhi * ct,
    });

    const p1 = e(cosTh1, sinTh1);
    const d1 = eDot(cosTh1, sinTh1);
    const p2 = e(cosTh2, sinTh2);
    const d2 = eDot(cosTh2, sinTh2);

    out.push([
      { x: p1.x + t * d1.x, y: p1.y + t * d1.y },
      { x: p2.x - t * d2.x, y: p2.y - t * d2.y },
      p2,
    ]);
    th = th2;
  }
  return out;
}

/** Una subruta aplanada. `closed` importa: un trazo cerrado une el ultimo con el primero. */
export interface SubPath {
  points: PointMm[];
  closed: boolean;
}

export function flattenPath(d: string): SubPath[] {
  const cmds = tokenize(d);
  const subPaths: SubPath[] = [];
  let current: SubPath | null = null;
  let cursor: PointMm = { x: 0, y: 0 };
  let start: PointMm = { x: 0, y: 0 };
  let lastCubicCtrl: PointMm | null = null;
  let lastQuadCtrl: PointMm | null = null;

  const push = (p: PointMm) => {
    if (!current) {
      current = { points: [{ ...cursor }], closed: false };
      subPaths.push(current);
    }
    current.points.push(p);
  };

  const curveTo = (p1: PointMm, p2: PointMm, p3: PointMm) => {
    for (let i = 1; i <= CURVE_SAMPLES; i++) push(bezierCubic(cursor, p1, p2, p3, i / CURVE_SAMPLES));
  };

  for (const cmd of cmds) {
    const rel = cmd.code === cmd.code.toLowerCase();
    const code = cmd.code.toUpperCase();
    const v = cmd.values;
    let i = 0;

    const rx = (n: number) => (rel ? cursor.x + n : n);
    const ry = (n: number) => (rel ? cursor.y + n : n);

    switch (code) {
      case 'M': {
        while (i < v.length) {
          const p = { x: rx(v[i]!), y: ry(v[i + 1]!) };
          i += 2;
          if (current === null || current.points.length > 1) {
            current = { points: [p], closed: false };
            subPaths.push(current);
          } else {
            current.points = [p];
          }
          cursor = p;
          start = p;
          // Los pares siguientes de una M son lineas implicitas.
          while (i + 1 < v.length) {
            const q = { x: rx(v[i]!), y: ry(v[i + 1]!) };
            i += 2;
            push(q);
            cursor = q;
          }
        }
        lastCubicCtrl = null;
        lastQuadCtrl = null;
        break;
      }
      case 'L': {
        while (i + 1 < v.length) {
          const p = { x: rx(v[i]!), y: ry(v[i + 1]!) };
          i += 2;
          push(p);
          cursor = p;
        }
        lastCubicCtrl = null;
        lastQuadCtrl = null;
        break;
      }
      case 'H': {
        while (i < v.length) {
          const p = { x: rx(v[i]!), y: cursor.y };
          i += 1;
          push(p);
          cursor = p;
        }
        lastCubicCtrl = null;
        lastQuadCtrl = null;
        break;
      }
      case 'V': {
        while (i < v.length) {
          const p = { x: cursor.x, y: ry(v[i]!) };
          i += 1;
          push(p);
          cursor = p;
        }
        lastCubicCtrl = null;
        lastQuadCtrl = null;
        break;
      }
      case 'C': {
        while (i + 5 < v.length) {
          const p1 = { x: rx(v[i]!), y: ry(v[i + 1]!) };
          const p2 = { x: rx(v[i + 2]!), y: ry(v[i + 3]!) };
          const p3 = { x: rx(v[i + 4]!), y: ry(v[i + 5]!) };
          i += 6;
          curveTo(p1, p2, p3);
          cursor = p3;
          lastCubicCtrl = p2;
          lastQuadCtrl = null;
        }
        break;
      }
      case 'S': {
        while (i + 3 < v.length) {
          const p1: PointMm = lastCubicCtrl
            ? { x: 2 * cursor.x - lastCubicCtrl.x, y: 2 * cursor.y - lastCubicCtrl.y }
            : { ...cursor };
          const p2 = { x: rx(v[i]!), y: ry(v[i + 1]!) };
          const p3 = { x: rx(v[i + 2]!), y: ry(v[i + 3]!) };
          i += 4;
          curveTo(p1, p2, p3);
          cursor = p3;
          lastCubicCtrl = p2;
          lastQuadCtrl = null;
        }
        break;
      }
      case 'Q': {
        while (i + 3 < v.length) {
          const p1 = { x: rx(v[i]!), y: ry(v[i + 1]!) };
          const p2 = { x: rx(v[i + 2]!), y: ry(v[i + 3]!) };
          i += 4;
          for (let s = 1; s <= CURVE_SAMPLES; s++) push(bezierQuad(cursor, p1, p2, s / CURVE_SAMPLES));
          cursor = p2;
          lastQuadCtrl = p1;
          lastCubicCtrl = null;
        }
        break;
      }
      case 'T': {
        while (i + 1 < v.length) {
          const p1: PointMm = lastQuadCtrl
            ? { x: 2 * cursor.x - lastQuadCtrl.x, y: 2 * cursor.y - lastQuadCtrl.y }
            : { ...cursor };
          const p2 = { x: rx(v[i]!), y: ry(v[i + 1]!) };
          i += 2;
          for (let s = 1; s <= CURVE_SAMPLES; s++) push(bezierQuad(cursor, p1, p2, s / CURVE_SAMPLES));
          cursor = p2;
          lastQuadCtrl = p1;
          lastCubicCtrl = null;
        }
        break;
      }
      case 'A': {
        while (i + 6 < v.length) {
          const arcRx = v[i]!;
          const arcRy = v[i + 1]!;
          const rot = v[i + 2]!;
          const largeArc = v[i + 3]! !== 0;
          const sweep = v[i + 4]! !== 0;
          const to = { x: rx(v[i + 5]!), y: ry(v[i + 6]!) };
          i += 7;
          for (const [p1, p2, p3] of arcToCubics(cursor, arcRx, arcRy, rot, largeArc, sweep, to)) {
            curveTo(p1, p2, p3);
            cursor = p3;
          }
          lastCubicCtrl = null;
          lastQuadCtrl = null;
        }
        break;
      }
      case 'Z': {
        if (current) {
          current.closed = true;
          cursor = { ...start };
          current = null;
        }
        lastCubicCtrl = null;
        lastQuadCtrl = null;
        break;
      }
      default:
        throw new Error(`path SVG: comando no admitido "${cmd.code}"`);
    }
  }

  return subPaths.filter((sp) => sp.points.length > 0);
}

/** Todos los puntos del path aplanado, sin distinguir subrutas. */
export function pathPoints(d: string): PointMm[] {
  return flattenPath(d).flatMap((sp) => sp.points);
}
